import classNames from "classnames";
import * as React from "react";
import { ReactElement } from "react";
import { TrainingCenterBundleItemTestQuestionId } from "../../messages/ObjectIds";
import { STRINGS } from "../../strings";
import { TrainingCenterCaregiverTestQuestion } from "../../system/AppState/AppStateTrainingCenter";
import { translate } from "../../translate";
import { HashMap } from "../../utils/HashMap";
import { List } from "../../utils/List";
import {
  lookupTranslationRequired,
  TrainingCenterLanguagePreference,
} from "./TrainingCenterLanguageLookup";

export interface TrainingCenterTestQuestionsListProps {
  disabled: boolean;
  testCompleted: boolean;
  showWrongAnswers: boolean;
  languagePreference: TrainingCenterLanguagePreference;
  questions: List<TrainingCenterCaregiverTestQuestion>;
  markedQuestionOverride: HashMap<TrainingCenterBundleItemTestQuestionId, number>;
  onQuestionAnswerClick: (
    bundleItemTestQuestionId: TrainingCenterBundleItemTestQuestionId,
    index: number
  ) => void;
}

export interface TrainingCenterTestQuestionsListMethods {
  scrollToQuestion(questionId: TrainingCenterBundleItemTestQuestionId): void;
}

export const TrainingCenterTestQuestionsList = React.memo(
  React.forwardRef(function TrainingCenterTestQuestionsList(
    props: TrainingCenterTestQuestionsListProps,
    ref: React.Ref<TrainingCenterTestQuestionsListMethods>
  ): ReactElement {
    const questionDomRefs = React.useRef(
      new RefsMap<TrainingCenterBundleItemTestQuestionId, HTMLDivElement>()
    );

    React.useImperativeHandle(
      ref,
      (): TrainingCenterTestQuestionsListMethods => ({
        scrollToQuestion: (questionId: TrainingCenterBundleItemTestQuestionId) => {
          const questionDomRef = questionDomRefs.current.get(questionId);
          if (questionDomRef.current !== null) {
            questionDomRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        },
      }),
      []
    );

    return (
      <div className="VideoScreen__test">
        <div className="__title">
          {translate(STRINGS.TEST)}
          <span className="__questionsAmount">{props.questions.length()} questions</span>
        </div>
        {props.questions.map((question) => {
          const markedOverride = props.markedQuestionOverride.get(question.questionId);

          return (
            <TrainingCenterTestQuestion
              key={TrainingCenterBundleItemTestQuestionId.unwrap(question.questionId)}
              outerDivRef={questionDomRefs.current.get(question.questionId)}
              disabled={props.disabled}
              testCompleted={props.testCompleted}
              showWrongAnswers={props.showWrongAnswers}
              languagePreference={props.languagePreference}
              question={question}
              markedOverride={markedOverride !== undefined ? markedOverride : null}
              onAnswerClick={props.onQuestionAnswerClick}
            />
          );
        })}
      </div>
    );
  })
);

class RefsMap<K, T> {
  public get(key: K): React.RefObject<T> {
    const val = this.map.get(key);
    if (val !== undefined) {
      return val;
    } else {
      const newRef = React.createRef<T>();
      this.map.set(key, newRef);
      return newRef;
    }
  }
  private map = new Map<K, React.RefObject<T>>();
}

interface TrainingCenterTestQuestionProps {
  outerDivRef?: React.RefObject<HTMLDivElement>;
  disabled: boolean;
  testCompleted: boolean;
  showWrongAnswers: boolean;
  languagePreference: TrainingCenterLanguagePreference;
  question: TrainingCenterCaregiverTestQuestion;
  markedOverride: number | null;
  onAnswerClick: (
    bundleItemTestQuestionId: TrainingCenterBundleItemTestQuestionId,
    index: number
  ) => void;
}

const TrainingCenterTestQuestion = React.memo(function TrainingCenterTestQuestion(
  props: TrainingCenterTestQuestionProps
): ReactElement {
  const handleAnswerClick = React.useCallback(
    (index: number) => {
      props.onAnswerClick.call(null, props.question.questionId, index);
    },
    [props.onAnswerClick, props.question.questionId]
  );

  const displayData = lookupTranslationRequired(
    props.languagePreference,
    props.question.displayData,
    (x) => x
  );

  const markedAnswer =
    props.markedOverride !== null ? props.markedOverride : props.question.markedAnswer;

  const rngSeed = TrainingCenterBundleItemTestQuestionId.unwrap(props.question.questionId);

  const showIncorrectAnswers: number[] = props.testCompleted
    ? markedAnswer === 0
      ? []
      : markedAnswer !== null
      ? [markedAnswer]
      : [] // Note: In this case, `markedAnswer` should always be non-null
    : props.showWrongAnswers
    ? props.question.incorrectlyGuessedAnswers
    : [];

  return (
    <ShuffledQuestion
      outerDivRef={props.outerDivRef}
      rngSeed={rngSeed}
      disabled={props.disabled}
      questionText={displayData.questionText}
      questionAnswers={displayData.questionAnswers}
      markedAnswer={markedAnswer}
      correctAnswer={props.question.markedAnswerCorrect ? 0 : null}
      incorrectAnswers={showIncorrectAnswers}
      onAnswerClick={handleAnswerClick}
    />
  );
});

interface QuestionProps {
  outerDivRef?: React.RefObject<HTMLDivElement>;
  disabled: boolean;
  questionText: string;
  questionAnswers: string[];
  markedAnswer: number | null;
  correctAnswer: number | null;
  incorrectAnswers: number[];
  onAnswerClick: (index: number) => void;
}

const Question = React.memo(function Question(props: QuestionProps): ReactElement {
  return (
    <div ref={props.outerDivRef} className="VideoScreen__question">
      <h3 className="VideoTestQuestion__text">{props.questionText}</h3>
      <div className="VideoScreen__questionAnswers">
        {props.questionAnswers.map((answer, index) => {
          const marked = props.markedAnswer === index;
          const incorrect = props.incorrectAnswers.indexOf(index) >= 0;
          const correct = props.correctAnswer === index;

          return (
            <div
              key={index}
              className={classNames({
                VideoScreen__questionAnwser: true,
                VideoScreen__questionAnwserChecked: incorrect,
                VideoScreen__questionAnswerError: incorrect,
              })}
              onClick={() => props.onAnswerClick(index)}
            >
              <div
                className={classNames({
                  "radio-button": true,
                  "radio-button-disabled":
                    props.disabled || incorrect || (props.correctAnswer !== null && !correct),
                })}
              >
                <span
                  className={classNames({
                    "radio-button__checkmark": true,
                    "radio-button__checkmarkCHECKED": marked,
                  })}
                />
              </div>
              <label className="VideoScreen__questionAnwserLabel">
                <span
                  style={{
                    textDecoration: incorrect ? "line-through" : undefined,
                  }}
                >
                  {answer}
                  {correct || incorrect ? (
                    <span
                      className={classNames({
                        VideoScreen__questionAnswerIndication: true,
                        VideoScreen__questionAnswerIndicationCorrect: correct,
                        VideoScreen__questionAnswerIndicationIncorrect: incorrect,
                      })}
                    >
                      {translate(correct ? STRINGS.CORRECT : STRINGS.INCORRECT)}
                    </span>
                  ) : null}
                </span>
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
});

interface ShuffledQuestionProps {
  outerDivRef?: React.RefObject<HTMLDivElement>;
  rngSeed: number;
  disabled: boolean;
  questionText: string;
  questionAnswers: string[];
  markedAnswer: number | null;
  correctAnswer: number | null;
  incorrectAnswers: number[];
  onAnswerClick: (index: number) => void;
}

const ShuffledQuestion = React.memo(function ShuffledQuestion(
  props: ShuffledQuestionProps
): ReactElement {
  const shuffledArray = React.useMemo(() => {
    const arr = [];
    for (let i = 0; i < props.questionAnswers.length; ++i) {
      arr.push(i);
    }
    shuffle(mulberry32(props.rngSeed), arr);
    return arr;
  }, [props.questionAnswers.length, props.rngSeed]);

  const shuffledAnswers = React.useMemo(() => {
    return shuffledArray.map((index) => props.questionAnswers[index]);
  }, [shuffledArray, props.questionAnswers]);

  const markedAnswer = React.useMemo(() => {
    return props.markedAnswer !== null ? shuffledArray.indexOf(props.markedAnswer) : null;
  }, [props.markedAnswer, shuffledArray]);

  const correctAnswer = React.useMemo(() => {
    return props.correctAnswer !== null ? shuffledArray.indexOf(props.correctAnswer) : null;
  }, [props.correctAnswer, shuffledArray]);

  const incorrectAnswers = React.useMemo(() => {
    return props.incorrectAnswers.map((index) => shuffledArray.indexOf(index));
  }, [props.incorrectAnswers, shuffledArray]);

  const handleAnswerClick = React.useCallback(
    (index: number) => {
      props.onAnswerClick.call(null, shuffledArray[index]);
    },
    [props.onAnswerClick, shuffledArray]
  );

  return (
    <Question
      outerDivRef={props.outerDivRef}
      disabled={props.disabled}
      questionText={props.questionText}
      questionAnswers={shuffledAnswers}
      markedAnswer={markedAnswer}
      correctAnswer={correctAnswer}
      incorrectAnswers={incorrectAnswers}
      onAnswerClick={handleAnswerClick}
    />
  );
});

/**
 * Shuffles array in place.
 *
 * @param rng A random number generate that returns a number in the range [0..1)
 * @param a items An array containing the items.
 */
function shuffle<T>(rng: () => number, a: T[]): void {
  let j;
  let x;
  let i;
  for (i = a.length - 1; i > 0; i--) {
    j = Math.floor(rng() * (i + 1));
    x = a[i];
    a[i] = a[j];
    a[j] = x;
  }
}

// """
// Mulberry32 is minimalistic generator utilizing a 32-bit state, originally
// intended for embedded applications. It appears to be very good; the author
// states it passes all tests of gjrand, and this JavaScript implementation is
// very fast. But since the state is 32-bit like Xorshift, it's period (how
// long the random sequence lasts before repeating) is significantly less than
// those with 128-bit states, but it's still quite large, at around 4 billion.
// """
//
// Taken from:
// <https://github.com/bryc/code/blob/master/jshash/PRNGs.md#mulberry32>
function mulberry32(seed: number): () => number {
  let seedv = seed;
  return (): number => {
    let t = (seedv += 0x6d2b79f5);
    t = (Math as any).imul(t ^ (t >>> 15), t | 1); // tslint:disable-line:no-bitwise
    t ^= t + (Math as any).imul(t ^ (t >>> 7), t | 61); // tslint:disable-line:no-bitwise
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296; // tslint:disable-line:no-bitwise
  };
}

// Math.imul polyfill
// From: <https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/imul>
if (!((Math as any).imul as boolean)) {
  (Math as any).imul = function imul(a: number, b: number) {
    const aHi = (a >>> 16) & 0xffff; // tslint:disable-line:no-bitwise
    const aLo = a & 0xffff; // tslint:disable-line:no-bitwise
    const bHi = (b >>> 16) & 0xffff; // tslint:disable-line:no-bitwise
    const bLo = b & 0xffff; // tslint:disable-line:no-bitwise
    // the shift by 0 fixes the sign on the high part
    // the final |0 converts the unsigned value into a signed value
    return (aLo * bLo + (((aHi * bLo + aLo * bHi) << 16) >>> 0)) | 0; // tslint:disable-line:no-bitwise
  };
}
